/**
 * Générateur — Automatisme : additionner, soustraire, multiplier des nombres
 * décimaux à une ou deux décimales
 * Attendu ID : 1045 | Niveau : 5ème | Domaine : Nombres relatifs (rubrique)
 * Rubrique : AUTOMATISME n°7 (programme 2025) — rangé sous « Nombres
 * relatifs » comme PRÉREQUIS des opérations sur relatifs, mais ne porte que
 * sur des décimaux POSITIFS : zéro signe moins dans les items, énoncés,
 * distracteurs et fiches (cadrage prof + source, 2026-09-01).
 * Labels (cartes exerciseur) :
 *   - Sommes et différences à deux décimales   (trous + QCM alignement)
 *   - Multiplier en réfléchissant              (saisie, familles énumérées)
 *   - Plus grand ou plus petit ?               (QCM, hors suivi 1045)
 *
 * ── Plan de cartes validé (2026-09-01, cadrage prof) ────────────────────────
 * Étend 1044 (add/sub une décimale) et 1042 (produits tables) là où ils
 * s'arrêtaient : DEUX décimales avec alignement en cœur, produit décimal ×
 * décimal en calcul réfléchi. TOUT EN CENTIÈMES ENTIERS (produits en
 * dix-millièmes) : zéro flottant, chaque égalité re-vérifiée (throw sinon).
 *
 * INVARIANTS :
 *   - bornes SOURCE (pas < 10) : opérandes < 20, résultats < 20, au plus UNE
 *     ligne par série avec un opérande ≥ 10, en ADDITION seulement
 *     (12,4 + 0,86) — jamais 17,68 − 8,89 (revue prof 2026-09-03) ;
 *   - AUTOMATISME, pas calcul posé : au plus UN franchissement (retenue ou
 *     emprunt) par ligne, compté rang par rang (nbFranchissements) ;
 *   - série de trous à TROIS lignes (revue prof 2026-09-03) : la ligne à
 *     UNE décimale des deux côtés avec franchissement (2,8 + 1,6 ; 3,4 − 0,8 :
 *     le cœur du programme préparé en CM2, expliqué en dixièmes), une ligne
 *     à deux décimales (retenue ou emprunt de centièmes, en alternance), et
 *     une ligne tournante : ALIGNEMENT (2 décimales ± 1 décimale : le geste
 *     « 1,4 = 1,40 ») ou résultat COURT (les centièmes s'annulent :
 *     l'écriture attendue est réduite, tue « il y a toujours 2 décimales »)
 *     — catégories ÉTANCHES par construction ; rappel brouillon en tête
 *     (écran seulement, l'impression le retire) ; les paires sacrifiées de
 *     1044 (2,7 + 1,4…) sont exclues ici aussi ;
 *   - résultat toujours affiché RÉDUIT (jamais « 0,10 »), saisie tolérante
 *     (strict:false : 0,1 et 0,10 acceptés) — contrat commun F1/F3 ;
 *   - QCM alignement : support 2 décimales ± 1 décimale, distracteurs
 *     mécanisés re-vérifiés distincts et positifs ;
 *   - produits : AUCUN tirage libre. Famille de BASE « table + virgule »
 *     (revue prof 2026-09-03 : un fait de table, puis je compte les
 *     décimales : 0,7 × 0,8 ; 0,06 × 7 ; 3 × 0,04 ; 0,9 × 20 ; 0,12 × 0,3),
 *     une fois sur deux, seule à pouvoir donner TROIS décimales (jamais
 *     quatre) ; familles réfléchies pour l'autre moitié (moitié, quart, trois
 *     quarts, ×1,5, ×2,5, ×1,25, carrés de demis, ×1,5 mixte, distributivité
 *     entier × décimal 3 × 2,4), résultat ≤ 2 décimales par assert ;
 *   - F4 « Plus grand ou plus petit ? » (revue prof 2026-09-03) : la
 *     comparaison du produit au plus GRAND facteur, seule à travailler la
 *     misconception « multiplier agrandit » ; l'encadrement « entre quels
 *     entiers ? » a été supprimé (revenait à calculer puis cocher). Carte
 *     SÉPARÉE cochée par le prof, résultats hors suivi 1045 :
 *     contenu.cadence = 'controle_ordre_grandeur' (clé de format et cadence
 *     conservées : persistées avec les questions existantes) ;
 *   - PAIRES SACRIFIÉES aux fiches : 2,75 + 1,4 (et ses soustractions liées
 *     4,15 − 1,4 ; 4,15 − 2,75), les produits 1,5 × 4 et 4,5 × 1,5, plus la
 *     paire des fiches 1044 (2,7 + 1,4 et liées) ne sortent JAMAIS
 *     (invariant protégé par cdp1045.self.test.js).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';
import { PAIRE_SACRIFIEE as PAIRE_1044 } from '../operations/gen_1044';

const ATTENDU_ID = 1045;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_SOMMES = 'Sommes et différences à deux décimales';
const L_PRODUITS = 'Multiplier en réfléchissant';
const L_CONTROLE = 'Plus grand ou plus petit ?';

/** Rappel, même forme que les gens 6e (613, 615, 619), écran seulement.
 *  Formulé « de tête, ou au brouillon » : c'est un automatisme, le brouillon
 *  est une aide, pas la méthode (revue 2026-09-03). */
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Calcule de tête, ou pose au brouillon si tu en as besoin.</p>';

// ─── Le domaine (centièmes entiers) ───────────────────────────────────────

/** La paire de la fiche sommes, sacrifiée (en centièmes) : 2,75 + 1,4. */
export const PAIRE_SACRIFIEE = { a100: 275, b100: 140 };

/** Le produit de la fiche, sacrifié (en centièmes) : 1,5 × 4. */
export const PRODUIT_SACRIFIE = { a100: 150, b100: 400 };

/** Le produit de la page « deux nombres à virgule » de la fiche, sacrifié :
 *  4,5 × 1,5 (45 dixièmes × 15 dixièmes = 675 centièmes), revue prof 2026-09-03. */
export const PRODUIT_SACRIFIE_2 = { a100: 450, b100: 150 };

/** Une opération ± est sacrifiée si elle recompose l'exemple des fiches :
 *  2,75 + 1,4 (deux ordres), 4,15 − 1,4, 4,15 − 2,75 — ou l'exemple des
 *  fiches de 1044 (2,7 + 1,4 ; 4,1 − 1,4 ; 4,1 − 2,7), puisque la ligne à
 *  une décimale vit ici aussi (revue prof 2026-09-03). */
const estPaire = (op, x100, y100, a100, b100) => {
    if (op === '+') return (x100 === a100 && y100 === b100) || (x100 === b100 && y100 === a100);
    return x100 === a100 + b100 && (y100 === a100 || y100 === b100);
};
const estSacrifiee = (op, x100, y100) => estPaire(op, x100, y100, PAIRE_SACRIFIEE.a100, PAIRE_SACRIFIEE.b100)
    || estPaire(op, x100, y100, PAIRE_1044.a * 10, PAIRE_1044.b * 10);

/** Nombre de franchissements (retenues en +, emprunts en −), rang par rang
 *  sur les centièmes entiers. AUTOMATISME = au plus UN (revue prof
 *  2026-09-03 : 5,23 − 1,46 ou 3,85 + 1,29 mentalement, c'est du posé). */
export const nbFranchissements = (op, x100, y100) => {
    let report = 0;
    let n = 0;
    for (let rang = 0; rang < 4; rang++) {
        const dx = Math.floor(x100 / 10 ** rang) % 10;
        const dy = Math.floor(y100 / 10 ** rang) % 10;
        if (op === '+') {
            const s = dx + dy + report;
            report = s >= 10 ? 1 : 0;
        } else {
            const d = dx - dy - report;
            report = d < 0 ? 1 : 0;
        }
        n += report;
    }
    return n;
};

/** Filtre des tirages : ni sacrifiée, ni deux franchissements. */
const acceptable = (op, x100, y100) => !estSacrifiee(op, x100, y100) && nbFranchissements(op, x100, y100) <= 1;

const estProduitSacrifie = (a100, b100) => [PRODUIT_SACRIFIE, PRODUIT_SACRIFIE_2]
    .some((s) => (a100 === s.a100 && b100 === s.b100) || (a100 === s.b100 && b100 === s.a100));

/** n centièmes → écriture canonique RÉDUITE (« 4,15 », « 4,2 », « 4 »). */
const fmtCent = (n100) => {
    const ent = Math.floor(n100 / 100);
    const c = String(n100 % 100).padStart(2, '0').replace(/0+$/, '');
    return c === '' ? String(ent) : `${ent},${c}`;
};

/** Écriture française → centièmes entiers (re-parse de vérification). */
const parseCent = (s) => {
    const m = String(s).match(/^(\d+)(?:,(\d{1,2}))?$/);
    if (!m) return null;
    return parseInt(m[1], 10) * 100 + parseInt(((m[2] || '') + '00').slice(0, 2), 10);
};

/** AUTO-VÉRIFICATION d'une opération ± émise, par re-parse des écritures. */
const verifierOperation = (op, x100, y100, r100) => {
    const gauche = op === '+' ? x100 + y100 : x100 - y100;
    if (gauche !== r100 || r100 <= 0 || r100 >= 2000
        || x100 < 1 || y100 < 1 || x100 >= 2000 || y100 >= 2000
        || parseCent(fmtCent(x100)) !== x100 || parseCent(fmtCent(y100)) !== y100
        || parseCent(fmtCent(r100)) !== r100) {
        throw new Error(`gen_1045 : opération invalide ${x100} ${op} ${y100} = ${r100}`);
    }
    if (estSacrifiee(op, x100, y100)) throw new Error('gen_1045 : paire sacrifiée tirée');
    if (nbFranchissements(op, x100, y100) > 1) throw new Error(`gen_1045 : deux franchissements ${x100} ${op} ${y100}`);
    if (op === '-' && (x100 >= 1000 || y100 >= 1000)) throw new Error(`gen_1045 : grande ligne en soustraction ${x100} - ${y100}`);
    return { op, x100, y100, r100 };
};

const enTex = (s) => String(s).replace(',', '{,}');
const ligneTex = (it) => `${enTex(fmtCent(it.x100))} ${it.op === '+' ? '+' : '-'} ${enTex(fmtCent(it.y100))}`;

// ─── Tirages ± par catégorie (couverture prof, catégories ÉTANCHES) ────────
// Étanchéité : une_decimale = 1 décimale stricte des DEUX côtés ;
// add_retenue/sub_emprunt = 2 vraies décimales des deux côtés et résultat
// NON réduit ; alignement = exactement UN opérande à 1 décimale stricte ;
// resultat_court = 2 vraies décimales des deux côtés et centièmes qui
// s'annulent (résultat réduit). Classification unique par construction.
// Partout : au plus UN franchissement (filtre `acceptable`), et la grande
// ligne (opérande ≥ 10) n'existe qu'en ADDITION.

const tirerCategorie = (categorie, grandAutorise) => {
    const entMax = grandAutorise ? 17 : 8;
    for (let essai = 0; essai < 80; essai++) {
        if (categorie === 'une_decimale') {
            // Le cœur du programme (2,7 + 1,4 ; 3,4 − 0,8) : deux opérandes à
            // une décimale, inférieurs à 10, franchissement de l'unité.
            if (randomChoice([true, false])) {
                const da = randInt(1, 9);
                const db = randInt(Math.max(1, 10 - da), 9);
                const x = randInt(0, 8) * 100 + da * 10;
                const y = randInt(0, 8) * 100 + db * 10;
                if (x + y >= 1000 || !acceptable('+', x, y)) continue;
                return verifierOperation('+', x, y, x + y);
            }
            // da ≤ 8 : il faut un db strictement plus grand (l'emprunt).
            const da = randInt(1, 8);
            const db = randInt(da + 1, 9);
            const ea = randInt(1, 9);
            const x = ea * 100 + da * 10;
            const y = randInt(0, ea - 1) * 100 + db * 10;
            if (x >= 1000 || !acceptable('-', x, y)) continue;
            return verifierOperation('-', x, y, x - y);
        }
        if (categorie === 'add_retenue') {
            // ca + cb ≥ 11 (jamais 10 : ce serait un résultat court) ; les
            // dixièmes n'enchaînent pas de seconde retenue.
            const ca = randInt(2, 9);
            const cb = randInt(Math.max(2, 11 - ca), 9);
            if (ca + cb < 11) continue;
            const ea = randInt(0, entMax);
            // Grande ligne (ea ≥ 10) : des dixièmes non nuls des deux côtés,
            // sinon 15,32 + 0,09 est trop molle pour justifier un opérande ≥ 10.
            const da = ea >= 10 ? randInt(1, 7) : randInt(0, 8);
            const db = ea >= 10 ? randInt(1, 8 - da) : randInt(0, 8 - da);
            const eb = randInt(0, ea >= 10 ? 1 : 8);
            const x = ea * 100 + da * 10 + ca;
            const y = eb * 100 + db * 10 + cb;
            if (x + y >= 2000 || !acceptable('+', x, y)) continue;
            return verifierOperation('+', x, y, x + y);
        }
        if (categorie === 'sub_emprunt') {
            // ca < cb : emprunt au rang des centièmes, résultat non réduit ;
            // les dixièmes ne réempruntent pas.
            const ca = randInt(1, 8);
            const cb = randInt(ca + 1, 9);
            const da = randInt(1, 9);
            const db = randInt(0, da - 1);
            const ea = randInt(1, 8);
            const eb = randInt(0, ea - 1);
            const x = ea * 100 + da * 10 + ca;
            const y = eb * 100 + db * 10 + cb;
            if (!acceptable('-', x, y)) continue;
            return verifierOperation('-', x, y, x - y);
        }
        if (categorie === 'alignement') {
            // Le geste « 1,4 = 1,40 » : UN opérande à 1 décimale stricte.
            const op = randomChoice(['+', '-']);
            const a2 = randInt(0, 8) * 100 + randInt(0, 9) * 10 + randInt(1, 9);
            const b1 = randInt(0, op === '+' ? entMax : 8) * 100 + randInt(1, 9) * 10;
            if (op === '+') {
                if (a2 + b1 >= 2000 || !acceptable('+', a2, b1)) continue;
                // Ordre varié : le 1-décimale peut ouvrir la ligne (12,4 + 0,86).
                const [x, y] = randomChoice([[a2, b1], [b1, a2]]);
                return verifierOperation('+', x, y, x + y);
            }
            const x = Math.max(a2, b1);
            const y = Math.min(a2, b1);
            if (x === y || !acceptable('-', x, y)) continue;
            return verifierOperation('-', x, y, x - y);
        }
        // 'resultat_court' : les centièmes s'annulent, l'écriture attendue
        // est réduite (2,35 + 1,45 = 3,8 ; 3,45 − 1,25 = 2,2).
        const ca = randInt(1, 9);
        if (randomChoice([true, false])) {
            const cb = 10 - ca;
            if (cb < 1 || cb > 9) continue;
            const da = randInt(0, 8);
            const db = randInt(0, 8 - da);
            const x = randInt(0, 8) * 100 + da * 10 + ca;
            const y = randInt(0, 8) * 100 + db * 10 + cb;
            if (x < 1 || y < 1 || x + y >= 2000 || !acceptable('+', x, y)) continue;
            return verifierOperation('+', x, y, x + y);
        }
        const ea = randInt(1, 8);
        const eb = randInt(0, ea - 1);
        const x = ea * 100 + randInt(0, 9) * 10 + ca;
        const y = eb * 100 + randInt(0, 9) * 10 + ca;
        if (y < 1 || x <= y || !acceptable('-', x, y)) continue;
        return verifierOperation('-', x, y, x - y);
    }
    // Repli déterministe sûr par catégorie (jamais une paire sacrifiée, un
    // seul franchissement).
    if (categorie === 'une_decimale') return verifierOperation('+', 280, 160, 440);
    if (categorie === 'add_retenue') return verifierOperation('+', 325, 147, 472);
    if (categorie === 'sub_emprunt') return verifierOperation('-', 543, 126, 417);
    if (categorie === 'alignement') return verifierOperation('+', 1240, 86, 1326);
    return verifierOperation('+', 235, 145, 380);
};

/** Verbalisation spécifique à l'item (l'explication d'après) : en dixièmes
 *  pour la ligne à une décimale, en centièmes sinon. */
const expliquerItem = (it) => {
    const mot = it.op === '+' ? 'plus' : 'moins';
    if (it.x100 % 10 === 0 && it.y100 % 10 === 0) {
        return `${fmtCent(it.x100)} c'est ${it.x100 / 10} dixièmes, ${mot} ${it.y100 / 10} dixièmes : `
            + `${it.r100 / 10} dixièmes = ${fmtCent(it.r100)}.`;
    }
    return `${fmtCent(it.x100)} c'est ${it.x100} centièmes, ${mot} ${it.y100} centièmes : `
        + `${it.r100} centièmes = ${fmtCent(it.r100)}.`;
};

// ─── Carte 1, format A : la série à trous ─────────────────────────────────

// TROIS lignes par série (revue prof 2026-09-03) : la ligne à UNE décimale
// (le cœur du programme, restitution rapide), une ligne à deux décimales
// (retenue ou emprunt en alternance), une ligne tournante (alignement ou
// résultat court).
const CATS_DEUX_DEC = ['add_retenue', 'sub_emprunt'];
const CATS_TOURNANTES = ['alignement', 'resultat_court'];
const NB_LIGNES = 3;

const genSommesTrous = () => {
    for (let essai = 0; essai < 60; essai++) {
        const cats = ['une_decimale', randomChoice(CATS_DEUX_DEC), randomChoice(CATS_TOURNANTES)];
        // Au plus UNE ligne avec un opérande ≥ 10 (addition seulement, la
        // catégorie décide) : un seul slot y est autorisé, jamais la ligne à
        // une décimale.
        const slotGrand = randomChoice([-1, 1, 2]);
        const items = cats.map((c, i) => tirerCategorie(c, i === slotGrand));
        if (items.filter((it) => it.x100 >= 1000 || it.y100 >= 1000).length > 1) continue;
        // Au moins une addition ET une soustraction par série (revue 2026-09-03).
        if (new Set(items.map((it) => it.op)).size < 2) continue;
        const cles = new Set(items.map((it) => `${it.op}|${it.x100}|${it.y100}`));
        const resultats = new Set(items.map((it) => it.r100));
        if (cles.size < NB_LIGNES || resultats.size < NB_LIGNES) continue;
        const melange = shuffle(items);
        const phrase = melange.map((it) => `$${ligneTex(it)} = {{${fmtCent(it.r100)}}}$`).join('\n');
        const payload = makeTexteTrou(
            phrase,
            `${RAPPEL_BROUILLON}<p><b>Calcule chaque somme ou différence.</b></p>`,
            { ...opts(L_SOMMES), mode: 'saisie' },
        );
        payload.contenu = { ...payload.contenu, explications: melange.map(expliquerItem) };
        payload._meta.params = { items: melange };
        return payload;
    }
    const items = [
        verifierOperation('+', 280, 160, 440),
        verifierOperation('-', 543, 126, 417),
        verifierOperation('+', 1240, 86, 1326),
    ];
    const payload = makeTexteTrou(
        items.map((it) => `$${ligneTex(it)} = {{${fmtCent(it.r100)}}}$`).join('\n'),
        `${RAPPEL_BROUILLON}<p><b>Calcule chaque somme ou différence.</b></p>`,
        { ...opts(L_SOMMES), mode: 'saisie' },
    );
    payload.contenu = { ...payload.contenu, explications: items.map(expliquerItem) };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 1, format B : QCM alignement ───────────────────────────────────

/** Feedback DORMANT par motif : la version « après » des misconceptions. */
const EXPLICATIONS_QCM = {
    alignement_droite: 'On aligne les VIRGULES, pas les derniers chiffres : 1,4 c\'est 1,40, donc 140 centièmes.',
    zero_intercale: 'Dans 1,4 le 4 est le chiffre des dixièmes : 1,4 c\'est 1,40, pas 1,04.',
    retenue_perdue: 'Les centièmes dépassent une unité : elle passe à gauche.',
    emprunt_non_retranche: 'L\'unité empruntée doit être retirée de la partie entière.',
};

/** Les trois explications qui s'appliquent à l'opération de l'item. */
const explicationsQcmPour = (op) => {
    const { alignement_droite, zero_intercale, retenue_perdue, emprunt_non_retranche } = EXPLICATIONS_QCM;
    return op === '+'
        ? { alignement_droite, zero_intercale, retenue_perdue }
        : { alignement_droite, zero_intercale, emprunt_non_retranche };
};

const distracteursPour = (it) => {
    // Support garanti par le tirage : x à 2 vraies décimales, y à 1 décimale
    // stricte (le distracteur d'alignement n'existe que là).
    const ey = Math.floor(it.y100 / 100);
    const dy = Math.floor((it.y100 % 100) / 10);
    if (it.op === '+') {
        return [
            // 2,75 + 1,4 → 2,89 : le 4 aligné sous le 5.
            { texte: fmtCent(it.x100 + it.y100 / 10), code: 'alignement_droite' },
            // 1,4 lu comme 1,04.
            { texte: fmtCent(it.x100 + ey * 100 + dy), code: 'zero_intercale' },
            { texte: fmtCent(it.r100 - 100), code: 'retenue_perdue' },
        ];
    }
    return [
        { texte: fmtCent(it.x100 - it.y100 / 10), code: 'alignement_droite' },
        { texte: fmtCent(it.x100 - (ey * 100 + dy)), code: 'zero_intercale' },
        { texte: fmtCent(it.r100 + 100), code: 'emprunt_non_retranche' },
    ];
};

const genAlignementQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const op = randomChoice(['+', '-']);
        // x : 2 vraies décimales, < 10 ; y : 1 décimale stricte, < 10.
        const x = randInt(0, 8) * 100 + randInt(0, 9) * 10 + randInt(1, 9);
        const y = randInt(0, 8) * 100 + randInt(1, 9) * 10;
        if (x < 1 || y < 1) continue;
        let it;
        if (op === '+') {
            // Support : retenue d'unité (sinon retenue_perdue < 1er opérande,
            // distracteur incohérent) : les mantisses dépassent 100.
            if ((x % 100) + (y % 100) < 100) continue;
            // …et cette retenue est la seule (2,18 + 7,90 en aurait deux).
            if (!acceptable('+', x, y)) continue;
            it = verifierOperation('+', x, y, x + y);
        } else {
            // Support : emprunt d'unité (mantisse de x < mantisse de y).
            if (x <= y || (x % 100) >= (y % 100)) continue;
            if (!acceptable('-', x, y)) continue;
            it = verifierOperation('-', x, y, x - y);
        }
        const faux = distracteursPour(it);
        if (faux.some((f) => parseCent(f.texte) === null || parseCent(f.texte) <= 0)) continue;
        const textes = [fmtCent(it.r100), ...faux.map((f) => f.texte)];
        if (new Set(textes).size !== 4) continue;
        const payload = makeQcm(
            textes,
            fmtCent(it.r100),
            `<p><b>Combien vaut ${tex(ligneTex(it))} ?</b></p>`,
            opts(L_SOMMES),
            false,
        );
        // sousType : la fiche sert l'exemple de la même opération (variantes).
    payload.contenu = { ...payload.contenu, sousType: it.op === '+' ? 'addition' : 'soustraction', explications: explicationsQcmPour(it.op) };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe (retenue d'unité : 85 + 30 = 115 centièmes).
    const it = verifierOperation('+', 385, 130, 515);
    const faux = distracteursPour(it);
    const payload = makeQcm(
        [fmtCent(it.r100), ...faux.map((f) => f.texte)],
        fmtCent(it.r100),
        `<p><b>Combien vaut ${tex(ligneTex(it))} ?</b></p>`,
        opts(L_SOMMES),
        false,
    );
    // sousType : la fiche sert l'exemple de la même opération (variantes).
    payload.contenu = { ...payload.contenu, sousType: it.op === '+' ? 'addition' : 'soustraction', explications: explicationsQcmPour(it.op) };
    payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2 : le produit (table + virgule en base, familles réfléchies) ──
// Valeurs en CENTIÈMES ; produit en DIX-MILLIÈMES (r4). AUCUN tirage libre.
// Revue prof 2026-09-03 : la famille de BASE est « un fait de table, puis
// je compte les décimales » (0,7 × 0,8 ; 0,06 × 7 ; 3 × 0,04 ; 0,9 × 20 ;
// 0,12 × 0,3), tirée une fois sur deux ; elle seule peut donner TROIS
// décimales (jamais quatre). Les familles réfléchies (moitié, quart, ×1,5…)
// se partagent l'autre moitié, résultat ≤ 2 décimales, et gagnent la
// distributivité entier × décimal (3 × 2,4 = 3 × 2 + 3 × 0,4).

/** Dix-millièmes → écriture réduite (« 0,036 », « 0,56 », « 18 »). */
const fmtDixMill = (r4) => {
    const ent = Math.floor(r4 / 10000);
    const dec = String(r4 % 10000).padStart(4, '0').replace(/0+$/, '');
    return dec === '' ? String(ent) : `${ent},${dec}`;
};

// Le facteur « spécial » est `a` (ou le premier de la paire) ; l'autre
// facteur s'appelle b. Le geste post-réponse est INSTANCIÉ avec les nombres
// (gesteReflechi), plus une phrase générique (revue prof 2026-09-03).
const FAMILLES_REFLECHIES = {
    moitie: { a: 50, bs: [40, 60, 80, 120, 240, 480, 140, 280, 600, 800, 1200] },
    quart: { a: 25, bs: [400, 800, 1200, 1600, 40, 80, 120, 200] },
    trois_quarts: { a: 75, bs: [400, 800, 40, 200] },
    un_et_demi: { a: 150, bs: [200, 300, 600, 800, 20, 40, 60] },
    deux_et_demi: { a: 250, bs: [200, 400, 600, 800, 40, 120] },
    un_virgule_vingt_cinq: { a: 125, bs: [400, 800, 80] },
    carres_de_demis: { paires: [[50, 50], [150, 150], [250, 250]] },
    // 1,5 × 4,5 est sacrifié à la fiche (PRODUIT_SACRIFIE_2) : hors pool.
    fois_un_et_demi: { paires: [[150, 350], [150, 250]] },
    distributivite: {}, // entier × décimal à 1 décimale, tiré à part
};

const moitieDe = (n100) => fmtDixMill(n100 * 50);
const quartDe = (n100) => fmtDixMill(n100 * 25);

/** Le geste, avec les nombres de l'item (a = facteur spécial, b = l'autre). */
const gesteReflechi = (fam, a, b) => {
    const B = fmtCent(b);
    const r = fmtDixMill(a * b);
    switch (fam) {
        case 'moitie': return `0,5 × ${B}, c'est la moitié de ${B} : ${r}.`;
        case 'quart': return `0,25 × ${B}, c'est le quart de ${B} : ${r}.`;
        case 'trois_quarts': return `0,75 × ${B}, c'est trois quarts de ${B} : le quart, ${quartDe(b)}, trois fois : ${r}.`;
        case 'un_et_demi':
        case 'fois_un_et_demi':
            return `1,5 × ${B} = ${B} + la moitié de ${B} = ${B} + ${moitieDe(b)} = ${r}.`;
        case 'deux_et_demi': return `2,5 × ${B} = 2 × ${B} + la moitié de ${B} = ${fmtDixMill(b * 200)} + ${moitieDe(b)} = ${r}.`;
        case 'un_virgule_vingt_cinq': return `1,25 × ${B} = ${B} + le quart de ${B} = ${B} + ${quartDe(b)} = ${r}.`;
        case 'carres_de_demis': {
            const A = fmtCent(a);
            const ent = Math.floor(a / 100);
            if (ent === 0) return `0,5 × 0,5, c'est la moitié de 0,5 : ${r}.`;
            return `${A} × ${A} = ${A} × ${ent} + ${A} × 0,5 = ${fmtDixMill(a * ent * 100)} + ${moitieDe(a)} = ${r}.`;
        }
        default: return null;
    }
};

/** Mantisses de la famille « table + virgule » : les tables de 2 à 9, plus
 *  12 avec les petits multiplicateurs seulement (12 × 9 = 108 n'est pas un
 *  fait de table de 5e, revue 2026-09-03). */
const MANTISSES = [2, 3, 4, 5, 6, 7, 8, 9, 12];
const N_MAX_POUR_12 = 5;
/** Placements de la virgule (p, q) = nombre de décimales de chaque facteur :
 *  au moins une, au plus deux par facteur, au plus trois au total ; q = -1 :
 *  le second facteur est une dizaine entière, 20, 30 ou 40 (0,9 × 20 est
 *  l'exemple du programme ; 1,2 × 90 = 108 n'est plus un automatisme). */
const PLACEMENTS = [[1, 0], [0, 1], [2, 0], [0, 2], [1, 1], [2, 1], [1, 2], [1, -1], [2, -1]];
const DIZAINES = [2, 3, 4];

/** Écriture brute d'un entier N avec k décimales, zéros finaux GARDÉS
 *  (« 0,020 ») : l'étape que l'élève écrit vraiment avant de réduire. */
const placerVirgule = (N, k) => {
    if (k === 0) return String(N);
    const s = String(N).padStart(k + 1, '0');
    return `${s.slice(0, -k)},${s.slice(-k)}`;
};

const tirerTableVirgule = () => {
    for (let essai = 0; essai < 40; essai++) {
        const m = randomChoice(MANTISSES);
        const [p, q] = randomChoice(PLACEMENTS);
        const n = q === -1 ? randomChoice(DIZAINES) : randInt(2, m === 12 ? N_MAX_POUR_12 : 9);
        const a = m * 10 ** (2 - p); // centièmes
        const b = n * 10 ** (2 - q);
        if (a >= 2000 || (q >= 0 && b >= 2000)) continue;
        if (estProduitSacrifie(a, b)) continue;
        const r4 = a * b;
        if (r4 % 10 !== 0 || r4 >= 200000) continue; // jamais quatre décimales, résultat < 20
        // Le fait de table porte la dizaine entière quand il y en a une
        // (0,9 × 20 : « 9 × 20 = 180, une décimale : 18,0, c'est 18 »).
        const nbDec = p + Math.max(0, q);
        const brut = q === -1 ? m * n * 10 : m * n;
        const fait = q === -1 ? `${m} × ${n * 10} = ${brut}` : `${m} × ${n} = ${brut}`;
        const ecrit = placerVirgule(brut, nbDec);
        const reduit = fmtDixMill(r4);
        const pluriel = nbDec > 1 ? 's' : '';
        // Quand le fait finit par 0, l'étape « 0,020, c'est 0,02 » est montrée :
        // l'élève qui compte 3 décimales sur 20 écrit 0,020.
        const geste = ecrit === reduit
            ? `${fait}, ${nbDec} décimale${pluriel} en tout, donc ${reduit}.`
            : `${fait}, ${nbDec} décimale${pluriel} en tout : ${ecrit}, c'est ${reduit}.`;
        const [x, y] = randomChoice([[a, b], [b, a]]);
        return { fam: 'table_virgule', x100: x, y100: y, r4, geste };
    }
    return { fam: 'table_virgule', x100: 70, y100: 80, r4: 5600, geste: '7 × 8 = 56, 2 décimales en tout, donc 0,56.' };
};

const tirerReflechi = () => {
    for (let essai = 0; essai < 40; essai++) {
        const fam = randomChoice(Object.keys(FAMILLES_REFLECHIES));
        const def = FAMILLES_REFLECHIES[fam];
        let a; let b; let geste;
        if (fam === 'distributivite') {
            // k ≤ 5 : 9 × 4,6 = 36 + 5,4 n'est plus un automatisme (revue 2026-09-03).
            const k = randInt(2, 5);
            const e = randInt(1, 4);
            const d = randInt(1, 9);
            a = k * 100;
            b = e * 100 + d * 10;
            geste = `${k} × ${fmtCent(b)} = ${k} × ${e} + ${k} × ${fmtCent(d * 10)} = ${k * e} + ${fmtCent(k * d * 10)} = ${fmtCent(k * b)}.`;
        } else {
            [a, b] = def.paires ? randomChoice(def.paires) : [def.a, randomChoice(def.bs)];
            geste = gesteReflechi(fam, a, b);
        }
        if (estProduitSacrifie(a, b)) continue;
        const r4 = a * b;
        if (r4 % 100 !== 0) throw new Error(`gen_1045 : produit réfléchi à plus de 2 décimales ${a} × ${b}`);
        const [x, y] = randomChoice([[a, b], [b, a]]);
        return { fam, x100: x, y100: y, r4, geste };
    }
    return { fam: 'moitie', x100: 50, y100: 80, r4: 4000, geste: gesteReflechi('moitie', 50, 80) };
};

/** Tire un couple (a100, b100) et re-vérifie TOUT (throw sinon). */
const tirerProduit = () => {
    const p = randomChoice([true, false]) ? tirerTableVirgule() : tirerReflechi();
    if (p.x100 * p.y100 !== p.r4 || p.r4 <= 0 || p.r4 % 10 !== 0) throw new Error(`gen_1045 : produit invalide ${p.x100} × ${p.y100}`);
    // r100 : le résultat en centièmes quand il en a au plus deux (les
    // familles réfléchies, l'ordre de grandeur) ; null au-delà.
    return { ...p, r100: p.r4 % 100 === 0 ? p.r4 / 100 : null };
};

const genProduitReflechi = () => {
    const p = tirerProduit();
    const payload = makeSaisie(
        fmtDixMill(p.r4),
        `<p><b>Calcule : ${tex(`${enTex(fmtCent(p.x100))} \\times ${enTex(fmtCent(p.y100))}`)}</b></p>`,
        opts(L_PRODUITS),
    );
    // sousType : route la fiche (utils/coupDePouce.js, variantes) vers la page
    // qui correspond au tirage : deux nombres à virgule (4,5 × 1,5 ; 0,7 × 0,8)
    // ou un seul (1,5 × 4 ; 0,06 × 7). Champ STRUCTUREL, il survit au POST.
    const deuxVirgules = p.x100 % 100 !== 0 && p.y100 % 100 !== 0;
    payload.contenu = {
        ...payload.contenu,
        sousType: deuxVirgules ? 'deux_virgules' : 'une_virgule',
        explications: { geste: p.geste },
    };
    payload._meta.params = { ...p };
    return payload;
};

// ─── Carte 3 : le contrôle par ordre de grandeur (hors suivi 1045) ────────

// Revue prof 2026-09-03 : l'ENCADREMENT « entre quels entiers ? » est
// supprimé (nos lignes ont toutes un franchissement : « sans calculer »
// revenait à calculer, puis à cocher ; et la méthode « j'arrondis » donnait la
// mauvaise borne une fois sur deux). Reste la COMPARAISON, la seule qui
// travaille une vraie misconception (« multiplier agrandit toujours ») :
// on compare toujours au facteur le plus GRAND, c'est là qu'elle vit
// (0,9 × 20 comparé à 20 ; comparé à 0,9 la réponse est évidente).

const EXPLICATIONS_ODG = {
    comparaison_petit: 'Multiplier par un nombre plus petit que 1 rend le résultat plus petit.',
    comparaison_grand: 'Multiplier par un nombre plus grand que 1 rend le résultat plus grand.',
};

const genOdgComparaison = () => {
    // Polarité tirée D'ABORD (50/50), puis un couple compatible : « plus
    // petit » ne doit pas être toujours la bonne réponse (garde-fou prof).
    const polarite = randomChoice(['petit', 'grand']);
    for (let essai = 0; essai < 120; essai++) {
        const p = tirerProduit();
        // On compare au plus grand des deux facteurs ; le plus petit décide
        // (jamais 1 dans les pools, jamais d'égalité).
        const compare = Math.max(p.x100, p.y100);
        const facteur = Math.min(p.x100, p.y100);
        if (facteur === 100 || compare === facteur) continue;
        const estPetit = facteur < 100;
        if ((polarite === 'petit') !== estPetit) continue;
        const cTex = fmtCent(compare);
        // « Égal à » n'est JAMAIS la bonne réponse (aucune égalité possible :
        // l'autre facteur n'est jamais 1) : distracteur assumé, il capte
        // « multiplier ne change presque rien » et casse le 50/50 au hasard
        // (et le harnais exige 3 choix).
        const choix = [`Plus grand que ${cTex}`, `Plus petit que ${cTex}`, `Égal à ${cTex}`];
        const bonne = estPetit ? choix[1] : choix[0];
        const payload = makeQcm(
            choix,
            bonne,
            `<p><b>Sans calculer : ${tex(`${enTex(fmtCent(p.x100))} \\times ${enTex(fmtCent(p.y100))}`)}. Le résultat est-il plus grand ou plus petit que ${tex(enTex(cTex))} ?</b></p>`,
            opts(L_CONTROLE),
            false,
        );
        payload.contenu = {
            ...payload.contenu,
            cadence: 'controle_ordre_grandeur',
            explications: { comparaison: estPetit ? EXPLICATIONS_ODG.comparaison_petit : EXPLICATIONS_ODG.comparaison_grand },
        };
        payload._meta.params = { ...p, compare, facteur, polarite: estPetit ? 'petit' : 'grand' };
        return payload;
    }
    // Repli : 0,5 × 0,4 (item source) comparé au plus grand, 0,5.
    const payload = makeQcm(
        ['Plus grand que 0,5', 'Plus petit que 0,5', 'Égal à 0,5'],
        'Plus petit que 0,5',
        `<p><b>Sans calculer : ${tex('0{,}5 \\times 0{,}4')}. Le résultat est-il plus grand ou plus petit que ${tex('0{,}5')} ?</b></p>`,
        opts(L_CONTROLE),
        false,
    );
    payload.contenu = { ...payload.contenu, cadence: 'controle_ordre_grandeur', explications: { comparaison: EXPLICATIONS_ODG.comparaison_petit } };
    payload._meta.params = { fam: 'moitie', x100: 50, y100: 40, r4: 2000, r100: 20, compare: 50, facteur: 40, polarite: 'petit' };
    return payload;
};

// La clé de format `ordre_grandeur_qcm` et la cadence sont CONSERVÉES : elles
// sont persistées avec les questions déjà créées (generator_key).
const genOdg = genOdgComparaison;

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

/**
 * Chaque erreur est une MISCONCEPTION NOMMÉE d'élève (cadrage prof
 * 2026-09-01), mécanisée par opération, re-vérifiée ≠ bonne et positive.
 */
export const REGLES_DISTRACTEURS_QCM = {
    alignement_qcm: {
        support: '2 vraies décimales ± 1 décimale stricte, avec retenue (add) ou emprunt (sub) d\'unité — sinon retenue_perdue/emprunt_non_retranche dégénèrent',
        fixes: {
            alignement_droite: 'les derniers chiffres alignés au lieu des virgules (2,75 + 1,4 → 2,89) : LA misconception du niveau',
            zero_intercale: '1,4 lu comme 1,04 (le 4 pris pour des centièmes)',
            retenue_perdue: 'l\'unité de retenue perdue (→ résultat − 1)',
            emprunt_non_retranche: 'l\'emprunt fait aux dixièmes (4,15 − 1,4 : 15 < 40) mais jamais retiré des unités (→ résultat + 1)',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    sommes_texte_trou_saisie: genSommesTrous,
    alignement_qcm: genAlignementQcm,
    produit_reflechi_saisie: genProduitReflechi,
    ordre_grandeur_qcm: genOdg,
};

export const CATEGORIES = [
    { id: 'sommes2d', label: L_SOMMES, formatsList: [
        { key: 'sommes_texte_trou_saisie', label: 'Saisie trous' },
        { key: 'alignement_qcm', label: 'QCM' },
    ] },
    { id: 'produits', label: L_PRODUITS, formatsList: [
        { key: 'produit_reflechi_saisie', label: 'Saisie' },
    ] },
    { id: 'controle', label: L_CONTROLE, formatsList: [
        { key: 'ordre_grandeur_qcm', label: 'QCM' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
